<template>
  <div>
    <ReportHeaderTab />

    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h5 class="page-title">Trial Balance Report</h5>
        <p class="page-sub">Summary of all debit and credit balances for a period</p>
      </div>
      <div class="page-actions">

        <!-- Date Range — exact original filters -->
        <div class="date-range">
          <div class="date-field">
            <font-awesome-icon icon="calendar" class="date-icon" />
            <Datepicker
              v-model="tableData.start_date"
              :enableTimePicker="false"
              :hideInputIcon="true"
              class="date-picker"
              placeholder="Start date"
            />
          </div>
          <span class="date-sep">—</span>
          <div class="date-field">
            <font-awesome-icon icon="calendar" class="date-icon" />
            <Datepicker
              v-model="tableData.end_date"
              :enableTimePicker="false"
              :hideInputIcon="true"
              class="date-picker"
              placeholder="End date"
            />
          </div>
        </div>

        <button class="btn-primary-action" @click="fetchData()" :disabled="crudLoading">
          <span v-if="crudLoading" class="spinner-border spinner-border-sm me-1" role="status"></span>
          <font-awesome-icon v-else icon="search" class="me-1" />
          {{ crudLoading ? 'Loading...' : 'Search' }}
        </button>

        <div class="dropdown">
          <button class="btn-outline dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
            <font-awesome-icon icon="download" /> Export
          </button>
          <ul class="dropdown-menu dropdown-menu-end">
            <li><a class="dropdown-item" href="#" @click.prevent="exportPDF('trial_balance_report')">
              <font-awesome-icon icon="file-pdf" class="me-2 text-danger" /> PDF
            </a></li>
          </ul>
        </div>

      </div>
    </div>

    <!-- Skeleton -->
    <template v-if="loading">
      <div class="report-wrap">
        <div class="skeleton skeleton--letterhead mb-4"></div>
        <div class="skeleton skeleton--row mb-2" v-for="n in 8" :key="n"></div>
        <div class="skeleton skeleton--total mt-3"></div>
      </div>
    </template>

    <!-- Empty -->
    <div v-else-if="!hasData" class="data-card">
      <div class="state-block">
        <div class="state-icon">
          <font-awesome-icon icon="calculator" />
        </div>
        <p class="state-title">No report data</p>
        <p class="state-sub">Select a date range and click Search to generate the trial balance.</p>
      </div>
    </div>

    <!-- Report -->
    <div v-else class="report-wrap" ref="pdfContent">

      <!-- Letterhead -->
      <div class="report-letterhead">
        <div class="letterhead-left">
          <div class="letterhead-icon">
            <font-awesome-icon icon="calculator" />
          </div>
          <div>
            <h2 class="letterhead-title">Trial Balance</h2>
            <p class="letterhead-sub">
              {{ formatDate(tableData.start_date) }} — {{ formatDate(tableData.end_date) }}
            </p>
          </div>
        </div>
        <div class="letterhead-right">
          <!-- Balance check -->
          <div class="balance-pill" :class="isBalanced ? 'balance-pill--ok' : 'balance-pill--err'">
            <font-awesome-icon :icon="isBalanced ? 'check-circle' : 'exclamation-circle'" class="me-1" />
            {{ isBalanced ? 'Balanced' : 'Unbalanced' }}
          </div>
          <!-- Totals summary -->
          <div class="totals-bar">
            <div class="totals-bar-item">
              <span class="totals-bar-label">Total Debit</span>
              <span class="totals-bar-value totals-bar-value--red">{{ formatPrice(total_debit) }}</span>
            </div>
            <span class="totals-bar-sep">vs</span>
            <div class="totals-bar-item">
              <span class="totals-bar-label">Total Credit</span>
              <span class="totals-bar-value totals-bar-value--green">{{ formatPrice(total_credit) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="table-wrap">
        <table class="rep-table">
          <thead>
            <tr>
              <th>Account</th>
              <th class="tr" style="width:180px">Debit</th>
              <th class="tr" style="width:180px">Credit</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in details" :key="index">
              <td>
                <div class="acc-cell">
                  <span class="acc-code">{{ row.code }}</span>
                  <span class="acc-name">{{ row.name }}</span>
                </div>
              </td>
              <td class="tr">
                <span v-if="row.total_debit" class="debit-value fw6">{{ formatPrice(row.total_debit) }}</span>
                <span v-else class="muted">—</span>
              </td>
              <td class="tr">
                <span v-if="row.total_credit" class="credit-value fw6">{{ formatPrice(row.total_credit) }}</span>
                <span v-else class="muted">—</span>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td class="total-label">TOTAL</td>
              <td class="tr total-amount total-amount--red">{{ formatPrice(total_debit) }}</td>
              <td class="tr total-amount total-amount--green">{{ formatPrice(total_credit) }}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div class="rep-footer">Generated by Mapiibs Solution</div>

    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { reusableMixin } from '../../../../lib/reusableMixin';
import { useStore } from 'vuex';
import ReportHeaderTab from './components/reportheadertab.vue';
import moment from 'moment';

export default {
  components: { ReportHeaderTab },
  mixins: [reusableMixin],

  data() {
    return {
      details:      [],
      total_debit:  0,
      total_credit: 0,
      loading:      false,
      crudLoading:  false,
      query:        null,

      tableData: { page: 1,
        start_date: new Date(new Date().getFullYear(), 0, 1), // Jan 1 of current year
        end_date:   new Date(),
        draw: 0, length: 10, search: '', column: 0, dir: 'desc', id: '',
      },

      toast: useToast(),
      store: useStore(),
    };
  },

  computed: {
    permission() { return this.store.state.getCurrentUserPermission; },
    hasData()    { return this.details.length > 0; },
    isBalanced() { return Math.abs(this.total_debit - this.total_credit) < 0.01; },
  },

  watch: { query() { this.fetchData(); } },
  created() { this.fetchData(); },

  methods: {
    // Exact original fetchData logic
    fetchData(url = '/accounting/report/trial-balance') {
      this.loading     = true;
      this.crudLoading = true;
      this.tableData.query = this.query;
      if (this.crudLoading && this.query) { this.crudLoading = false; }
      this.tableData.draw++;
      axios.get(url, { params: this.tableData })
        .then(response => {
          this.loading = false; this.crudLoading = false;
          if (!response.data.status == 200) return;
          const d          = response.data.data;
          // Handle both array and object responses
          this.details      = Array.isArray(d.data) ? d.data : Object.values(d.data ?? {});
          this.total_debit  = d.total_debit  ?? 0;
          this.total_credit = d.total_credit ?? 0;
        })
        .catch(error => {
          this.loading = false; this.crudLoading = false;
          if ([400, 401, 403, 500].includes(error.response?.status)) {
            this.toast.error(error.response.data.message);
          }
        });
    },

    formatDate(date) { return moment(date).format('DD/MM/YYYY'); },

    formatPrice(value) {
      if (value === null || value === undefined) return '0.00';
      return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
  },
};
</script>

<style scoped>
.page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
.page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub     { font-size:13px; color:#7a90b0; margin:0; }
.page-actions { display:flex; align-items:center; flex-wrap:wrap; gap:8px; }

/* ── Date Range (exact original dual pickers) ── */
.date-range { display:flex; align-items:center; gap:6px; background:#fff; border:1px solid #dde5f0; border-radius:8px; padding:4px 10px; }
.date-field { position:relative; display:flex; align-items:center; }
.date-icon  { position:absolute; left:8px; color:#7a90b0; font-size:12px; z-index:2; pointer-events:none; }
.date-sep   { color:#aab8cc; font-size:13px; flex-shrink:0; }
:deep(.date-picker)       { border:none !important; background:transparent !important; box-shadow:none !important; }
:deep(.date-picker input) { border:none !important; font-size:13px; color:#1a2b45; padding:4px 4px 4px 24px !important; width:130px; }

.btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
.btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; transition:background .15s; }
.btn-outline:hover { background:#f4f7fb; }
.btn-outline::after { display:none !important; }
.me-1 { margin-right:4px; } .me-2 { margin-right:8px; }

/* ── Skeleton ── */
.skeleton     { border-radius:8px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
.skeleton--letterhead { height:90px; }
.skeleton--row        { height:18px; }
.skeleton--total      { height:24px; width:50%; margin-left:auto; }
.mb-2 { margin-bottom:8px; } .mb-4 { margin-bottom:16px; } .mt-3 { margin-top:12px; }

/* ── Empty ── */
.data-card  { background:#fff; border:1px solid #e4eaf2; border-radius:12px; }
.state-block { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
.state-icon  { width:60px; height:60px; border-radius:16px; background:rgba(0,74,173,.1); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
.state-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
.state-sub   { font-size:13px; color:#7a90b0; margin:0; }

/* ── Report Wrap ── */
.report-wrap { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:32px; }

/* ── Letterhead ── */
.report-letterhead { display:flex; align-items:flex-start; justify-content:space-between; gap:20px; padding-bottom:24px; border-bottom:2px solid #e4eaf2; margin-bottom:24px; flex-wrap:wrap; }
.letterhead-left   { display:flex; align-items:center; gap:16px; }
.letterhead-icon   { width:52px; height:52px; border-radius:14px; background:rgba(0,74,173,.1); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:22px; flex-shrink:0; }
.letterhead-title  { font-size:22px; font-weight:800; color:#1a2b45; margin:0 0 3px; }
.letterhead-sub    { font-size:13px; color:#7a90b0; margin:0; }
.letterhead-right  { display:flex; flex-direction:column; align-items:flex-end; gap:10px; }

.balance-pill      { display:inline-flex; align-items:center; border-radius:8px; padding:6px 14px; font-size:12.5px; font-weight:700; border:1px solid transparent; }
.balance-pill--ok  { background:#f0fdf4; color:#16a34a; border-color:#bbf7d0; }
.balance-pill--err { background:#fff0f0; color:#e03535; border-color:#fac5c5; }

.totals-bar              { display:flex; align-items:center; gap:14px; background:#f8fafc; border:1px solid #e4eaf2; border-radius:10px; padding:10px 16px; }
.totals-bar-item         { display:flex; flex-direction:column; gap:2px; }
.totals-bar-label        { font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; }
.totals-bar-value        { font-size:16px; font-weight:800; }
.totals-bar-value--red   { color:#e03535; }
.totals-bar-value--green { color:#16a34a; }
.totals-bar-sep          { font-size:12px; font-weight:700; color:#aab8cc; }

/* ── Table ── */
.table-wrap { overflow-x:auto; }
.rep-table  { width:100%; border-collapse:collapse; }
.rep-table thead th        { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:11px 14px; border-bottom:2px solid #e4eaf2; background:#f8fafc; }
.rep-table tbody tr        { border-bottom:1px solid #f0f4f8; transition:background .12s; }
.rep-table tbody tr:last-child { border-bottom:none; }
.rep-table tbody tr:hover  { background:#fafbfc; }
.rep-table tbody td        { padding:11px 14px; font-size:13.5px; }
.rep-table tfoot tr        { background:#f8fafc; border-top:2px solid #e4eaf2; }
.rep-table tfoot td        { padding:12px 14px; font-size:13.5px; }
.tr     { text-align:right; }
.fw6    { font-weight:700; }
.muted  { color:#aab8cc; }

/* ── Account Cell ── */
.acc-cell { display:flex; align-items:center; gap:9px; }
.acc-code { font-family:monospace; font-size:11px; font-weight:600; color:#7a90b0; background:#f4f7fb; border:1px solid #e4eaf2; border-radius:5px; padding:1px 6px; flex-shrink:0; }
.acc-name { font-size:13.5px; color:#1a2b45; }

.debit-value  { color:#e03535; }
.credit-value { color:#16a34a; }

/* ── Footer Row ── */
.total-label         { font-size:13px; font-weight:700; color:#1a2b45; letter-spacing:.04em; }
.total-amount        { font-size:15px; font-weight:800; }
.total-amount--red   { color:#e03535; }
.total-amount--green { color:#16a34a; }

/* ── Report Footer ── */
.rep-footer { text-align:center; margin-top:28px; padding-top:20px; border-top:1px solid #e4eaf2; font-size:12px; color:#aab8cc; }

/* ── Dropdown ── */
.dropdown-menu { border:1px solid #e4eaf2; border-radius:9px; box-shadow:0 8px 24px rgba(0,0,0,.08); padding:6px; min-width:160px; }
.dropdown-item { border-radius:6px; font-size:13px; padding:8px 12px; color:#1a2b45; }
.dropdown-item:hover { background:#f4f7fb; }

/* ── Print ── */
@media print {
  .page-header,.btn-primary-action,.btn-outline,.dropdown { display:none !important; }
  .report-wrap { border:none; padding:0; }
}

/* ── Responsive ── */
@media(max-width:767px) {
  .page-header,.page-actions { flex-direction:column; align-items:stretch; }
  .date-range { flex-wrap:wrap; }
  .report-letterhead { flex-direction:column; }
  .letterhead-right  { align-items:flex-start; }
  .report-wrap { padding:16px; }
}
</style>